// D2's item list + the total of a quoted refund. See README.md § Refund amount.
//
// This module no longer computes the offer (it used to sum selections + a hardcoded $3 bonus): the
// guest currency service quotes and reserves the refund — see useInitializeRefund — and is the only
// thing allowed to say what a claim is worth. The prices below are for RECOGNISING items in D2.
import { Locale, checkLocale } from '@toasttab/buffet-pui-locale-utilities'
import { formatCurrency, Currency } from '@toasttab/buffet-pui-number-utilities'
import { Money } from 'src/apollo/onlineOrdering'
import { CompletedOrderSelection } from 'src/public/components/online_ordering/types'

export interface RefundableItem {
  // CompletedOrderSelection.guid, which is what initializeRefund's `selectionIds` takes.
  // Deliberately NOT `externalId` (a nullable client-side correlation id) and NOT `itemGuid` (the
  // MENU ITEM, shared by every order of that dish): either would scope the reservation wrongly.
  selectionGuid: string
  name: string
  modifiers: string[]
  price: number // dollars; non-null on CompletedOrderSelection
}

export interface MissingItemInput {
  selectionGuid: string
}

export function mapCompletedOrderSelections(
  selections: readonly CompletedOrderSelection[] | null | undefined
): RefundableItem[] {
  if (!selections) return []
  return selections.map((sel) => ({
    selectionGuid: sel.guid,
    name: sel.name,
    modifiers: sel.modifiers?.map((m) => m.name) ?? [],
    // The pre-discount line price, which is what the receipt shows against each item, so a guest
    // scanning D2 sees the prices they were shown. Nullable, hence the post-discount fallback.
    price: sel.preDiscountPrice ?? sel.price
  }))
}

// Amounts only, so a quoted refund fits without importing that hook module into a pure helper.
interface RefundAmounts {
  baseAmount: Pick<Money, 'amount'>
  bonusAmount: Pick<Money, 'amount'>
}

// What Toast Cash is worth: reserved base + quoted bonus. Rounded because adding two IEEE floats
// reintroduces the cents error the decimal strings exist to avoid (39.7 + 3 → 42.699999999999996).
export function refundTotalAmount(refund: RefundAmounts): number {
  return (
    Math.round(
      (Number(refund.baseAmount.amount) + Number(refund.bonusAmount.amount)) *
        100
    ) / 100
  )
}

// Currency string from a dollars amount. Currency and locale are passed in, not read from the
// browser: getLocale() would format a UK guest's refund with a US symbol. Mirrors useFormatPrice,
// AUD narrowSymbol included. Pure, so this works outside React — components use the hook.
export function formatAmount(
  amount: number,
  currency: Currency,
  locale?: Locale
): string {
  return formatCurrency({ amount, currency }, checkLocale(locale), {
    currencyDisplay: currency === Currency.AUD ? 'narrowSymbol' : 'symbol'
  })
}
